<template>
  <v-container class="px-5 py-0" :class="{ 'pa-2': xsOnly }">
    <v-layout column class="position-relative">
      <v-layout justify-end v-if="xsOnly" class="mt-5">
        <order-summary />
      </v-layout>
      <v-layout
        justify-center
        :class="{
          'mt-6 mx-4 mb-8': smAndDown,
          'mt-14 mb-5': mdAndUp
        }">
        <app-dropdown
          :items="guestItems"
          :value="selectedGuestId"
          @change="(guestId) => selectedGuestId = guestId"
          :placeholder="$t('fields.guest')"
          itemText="name"
          itemValue="id"
          :title="mdAndUp"
          class="inner-padding"
          :class="{ 'max-width-500': smAndUp }"
          outlined
          singleLine
        />
      </v-layout>
      <subtitle
        :text="viewPhotographyPackages ? $t('catalog.photography_options') : $t('catalog.package_options')"
        :noDivider="xsOnly"
        appendSlot>
        <template v-slot:append>
          <app-dropdown
            :items="exchangeCurrencies"
            itemText="currency.name"
            returnObject
            :value="selectedCurrency"
            @change="(currency) => selectedCurrency = currency"
            class="max-width-100 no-inner-padding mt-0 pt-0"
            selectionClass="d-flex justify-end full-width"
            hideDetails
            singleLine
            rounded
            dense
            height="23"
            :disabled="state.inModal"
          />
        </template>
        <template v-slot:prepend v-if="viewPhotographyPackages">
          <v-btn @click="viewPhotographyPackages = false" icon :class="{ 'ml-3': smAndUp, 'mr-3': xsOnly }">
            <v-icon color="primary" large>mdi-arrow-left</v-icon>
          </v-btn>
        </template>
      </subtitle>
      <!-- Package list -->
      <v-layout justify-center class="pt-4" :class="{ 'pb-10': smAndUp }">
        <v-progress-circular v-if="state.loading" indeterminate color="accent" class="mt-10" />
        <v-layout v-else-if="packageList.length" class="max-width-1000 " wrap>
          <v-flex v-for="(p, i) in packageList" :key="i" xs12 md6>
            <v-layout
              @click="handlePackageClick(p)"
              :class="{ 'px-6 py-2 mx-8': smAndUp, 'px-4 py-1': xsOnly }"
              class="my-4 clickable package-wrapper"
              column
              justify-space-between>
              <span class="nick-heading break-word">{{ p.name }}</span>
              <span v-if="p.isPhotographyFolder" class="grey--text package-price mt-1">{{ $t('catalog.see_photography') }}</span>
              <span v-else class="package-price mt-1">{{ getPriceStr(p) }}</span>
            </v-layout>
          </v-flex>
        </v-layout>
        <div v-else class="pt-10 title grey--text">{{ $t('order.no_packages') }}</div>
      </v-layout>
      <!-- Checkout button -->
      <v-layout v-if="cart.cartProducts.length" justify-center :class="{ 'py-2 bottom-sticky': xsOnly }">
        <v-flex xs12 :class="{ 'max-width-450': smAndUp }">
          <main-button
            :text="$t('actions.checkout')"
            @click="goToCheckout()"
            :disabled="state.inModal || isBookingDisabled" />
        </v-flex>
      </v-layout>
    </v-layout>
    <add-package-modal v-if="selectedCurrency" ref="addModal" :triggerOpen="triggerAdd" :exchangeCurrency="selectedCurrency" />
  </v-container>
</template>

<script>
import routeNames from '@/router/routes'
import { mapState, mapGetters } from 'vuex'
import moneyFormatMixins from '@/mixins/MoneyFormat'
import vuetifyMixins from '@/mixins/Vuetify'
import { getGuestFullName } from '@/lib/helpers/Guest'

export default {
  data () {
    return {
      routeNames,
      addingPackage: null,
      triggerAdd: null,
      viewPhotographyPackages: false,
      rules: {
        required: (v) => !!v || this.$t('error.form.required')
      }
    }
  },
  computed: {
    ...mapState(['bookings', 'packages', 'cart', 'me', 'state']),
    ...mapGetters({
      exchangeCurrencies: 'me/exchangeCurrencies',
      defaultCurrency: 'me/defaultCurrency',
      localCurrencyId: 'me/localCurrencyId',
      photographyCategoryId: 'me/photographyCategoryId',
      bookingProfileGuest: 'bookings/bookingProfileGuest',
      extendedGuests: 'bookings/extendedGuests',
      accountPricingGroupId: 'guests/accountPricingGroupId',
      getApplicablePrice: 'guests/getApplicablePrice',
      isGuestDisabled: 'guests/getIsGuestDisabled',
      isBookingDisabled: 'bookings/isBookingDisabled',
      getQuantity: 'cart/getQuantity'
    }),
    selectedCurrency: {
      get () {
        return this.cart.selectedCurrency
      },
      set (currency) {
        this.$store.commit('cart/setSelectedCurrency', currency)
      }
    },
    selectedGuestId: {
      get () {
        return this.cart.selectedGuest
      },
      set (id) {
        this.$store.commit('cart/setSelectedGuest', id)
        this.$store.dispatch('guests/get', { id: this.selectedGuestId, save: true }).then(() => {
          const unapplicableSelectedProductsForGuest = this.cart.cartProducts.filter(cartProduct => {
            return !this.getPriceStr(cartProduct.product)
          })
          unapplicableSelectedProductsForGuest.forEach(cartProduct => {
            this.$store.commit('cart/removeProductInCart', cartProduct.product)
          })
        })
      }
    },
    packageList () {
      let list = this.packages.list.filter(p => {
        return this.getPriceStr(p) // Product with no applicable price will not display
      })
      list = list.filter(p => {
        return this.viewPhotographyPackages
          ? p.categoryId === this.photographyCategoryId
          : p.categoryId !== this.photographyCategoryId
      })
      if (!this.viewPhotographyPackages) {
        list.unshift({
          name: this.$t('catalog.photography_folder_name'),
          isPhotographyFolder: true
        })
      }
      return list
    },
    guestItems () {
      return this.extendedGuests.map((extendedGuest) => {
        return {
          ...extendedGuest.guest,
          disabled: this.isGuestDisabled(extendedGuest.guest),
          name: getGuestFullName(extendedGuest)
        }
      })
    }
  },
  methods: {
    handlePackageClick (p) {
      if (p.isPhotographyFolder) {
        this.viewPhotographyPackages = true
      } else {
        this.$store.commit('cart/setProduct', p)
        this.$store.commit('cart/setQuantity', this.getQuantity({ product: p }) || 0)
        this.triggerAdd = new Date()
      }
    },
    getPriceStr (p) {
      const price = p.pricingGroupWithPrices && this.getApplicablePrice(p.pricingGroupWithPrices)
        ? this.getApplicablePrice(p.pricingGroupWithPrices) / (this.selectedCurrency ? this.selectedCurrency.exchangeRate : 1)
        : null
      return typeof price === 'number'
        ? `${this.getQuantity({ product: p }) ? this.getQuantity({ product: p }) + 'x ' : ''}
          ${this.formatAmount(price)}
          ${this.selectedCurrency ? this.selectedCurrency.currency.name : ''}`
        : null
    },
    goToCheckout () {
      this.$router.push({ name: routeNames.summary.name })
    }
  },
  created () {
    this.$store.commit('cart/recoverCart')
    this.$store.dispatch('packages/list', {})
    if (!this.selectedCurrency) {
      this.selectedCurrency = this.defaultCurrency
    }
    this.$store.dispatch('bookings/get').then((b) => {
      if (!this.selectedGuestId) {
        const firstEnabledGuest = this.guestItems.find(item => !item.disabled)
        this.selectedGuestId = firstEnabledGuest ? firstEnabledGuest.id : null
      } else {
        this.$store.dispatch('guests/get', { id: this.selectedGuestId, save: true })
      }
    })
  },
  components: {
    OrderSummary: () => import('@/components/view-components/OrderSummary.vue'),
    AppDropdown: () => import('@/components/app-components/AppDropdown.vue'),
    Subtitle: () => import('@/components/view-components/Subtitle.vue'),
    MainButton: () => import('@/components/app-components/MainButton.vue'),
    AddPackageModal: () => import('@/components/view-components/AddPackageModal.vue')
  },
  mixins: [
    moneyFormatMixins,
    vuetifyMixins
  ]
}
</script>

<style lang="scss" scoped>
::v-deep {
  .inner-padding .v-input__control div .v-select__slot {
    padding: 12px 0px !important;
  }
  .no-inner-padding .v-input__control .v-input__slot {
    padding: 0px !important;
  }
}
.package-wrapper {
  border: 2px #E1E1E1 solid;
  border-radius: 1px;
  background-color: #F6F6F6;
  min-height: calc(100% - 32px);
}
.package-price {
  color: var(--v-primary-base);
  font-size: 20px;
  font-weight: bold;
}
</style>
